import React from 'react';
import { Alert, AlertTitle, Box, Button, Chip } from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import EmptyOperationalState from '../../../components/EmptyOperationalState';
import { colors } from '../../../design-system/tokens';

/**
 * ModernWash 소스 봉투 상태를 화면에 표시하는 단일 창구.
 *
 * computeSourceState 서술자(state)를 받아 상황별로 렌더한다:
 *   - blocking + kind 'empty'  → 큰 안내 카드 (연동 미활성/비연동/데이터 없음/권한 없음)
 *   - blocking + kind 'alert'  → 상단 오류 배너 + (재시도 가능하면) 다시 시도 버튼
 *   - !blocking + degraded     → 은근한 "응답 지연" 배지 (데이터는 그대로 표시)
 *   - 그 외(정상)              → 아무것도 그리지 않음
 *
 * blocking 여부는 화면이 판단해 표/카드 렌더를 건너뛴다. 이 컴포넌트는 표시만 담당한다.
 */
const SourceStatusBanner = ({ state, onRetry, sx }) => {
  if (!state) return null;

  // 정상 + 응답 지연 → 은근한 배지만
  if (!state.blocking) {
    if (!state.degraded) return null;
    return (
      <Box sx={{ mb: 1.5, ...sx }}>
        <Chip
          size="small"
          icon={<AccessTimeRoundedIcon />}
          label="응답 지연"
          aria-label="원천 응답 지연"
          sx={{
            height: 24,
            fontWeight: 700,
            fontSize: 12,
            bgcolor: colors.status.warning.bg,
            color: colors.status.warning.fg,
            border: `1px solid ${colors.status.warning.border}`,
            '& .MuiChip-icon': { color: colors.status.warning.fg },
          }}
        />
      </Box>
    );
  }

  const retryButton =
    state.retryable && onRetry ? (
      <Button color="inherit" size="small" onClick={onRetry}>
        다시 시도
      </Button>
    ) : undefined;

  // 큰 안내 카드 (준비 중 / 비연동 / 데이터 없음 / 권한 없음)
  if (state.kind === 'empty') {
    return (
      <Box sx={{ py: 2, ...sx }}>
        <EmptyOperationalState
          variant={state.variant || 'empty'}
          title={state.title}
          description={state.detail ? `${state.description} (${state.detail})` : state.description}
          actionLabel={retryButton ? '다시 시도' : undefined}
          onAction={retryButton ? onRetry : undefined}
        />
      </Box>
    );
  }

  // 상단 오류 배너 (일시 장애 / 인증 오류 / 원천 서버 오류 / 일반 오류)
  return (
    <Alert severity={state.severity || 'error'} action={retryButton} sx={{ mb: 2, ...sx }}>
      <AlertTitle sx={{ fontWeight: 800 }}>{state.title}</AlertTitle>
      {state.detail ? `${state.description} (${state.detail})` : state.description}
    </Alert>
  );
};

export default SourceStatusBanner;
